import Component from '@bythepixel/component-loader';
import { cookieIsSet, fadeOut } from '@Utility';

/**
 * Helper function used for calculating the bottom position of conflicting elements
 * This is used in desktop view and when the gdpr notice is NOT visible
 * elementOffsetFromBottom is an optional hard-coded value that is set on the HTML element, this differs between elements
 *
 * @param el
 * @param index
 * @param elementOffsetFromBottom
 * @param totalOffsetFromBottom
 * @param elementHeight
 * @returns {*}
 */
function calculateOffsetWithoutGdprElement(el, index, elementOffsetFromBottom, totalOffsetFromBottom, elementHeight) {
  // Disable eslint to allow assigning to el
  /* eslint-disable */
  if (index === 0) {
    // For the first conflicting element, the bottom position is the gdprOffset if it exists
    // Otherwise it's fixed to the bottom
    el.style.bottom = elementOffsetFromBottom === 0 ? 0 : `${elementOffsetFromBottom / 16}rem`;
  } else {
    // For other conflicting elements, the bottom position is the total totalOffsetFromBottom from other elements
    // Plus the element's elementOffsetFromBottom
    el.style.bottom = totalOffsetFromBottom === 0 && elementOffsetFromBottom === 0
      ? 0 :`${(totalOffsetFromBottom + elementOffsetFromBottom) / 16}rem`;
  }

  // For each element, update totalOffsetFromBottom with the element height and the element offset
  totalOffsetFromBottom += elementHeight + elementOffsetFromBottom;
  /* eslint-enable */

  return totalOffsetFromBottom;
}

export default class Gdpr extends Component {
  static selector = '[data-comp-gdpr]';

  constructor(containerEl) {
    super(containerEl);
    this.cssClasses = {
      conflictElementHidden: 'gdpr__element--hidden',
      desktop: 'gdpr--desktop-styling',
    };

    this.conflictingElements = document.querySelectorAll('[data-gdpr-conflict]');
    if (this.conflictingElements.length === 0 || !this.conflictingElements[0].dataset.gdprBreakpoint) {
      this.breakpoint = 768;
    } else {
      this.breakpoint = this.conflictingElements[0].dataset.gdprBreakpoint;
    }

    this.cookieName = 'gbGdprNotice';
    if (cookieIsSet(this.cookieName)) {
      this.handleConflictingElements();
      this.displayConflictingElements();

      return;
    }

    this.button = this.$container.querySelector('[data-gdpr-button]');
    this.events();
  }

  addMatchMediaListener() {
    const handleMatchMediaChanges = this.handleMatchMediaChanges.bind(this);
    window.matchMedia(`(min-width: ${this.breakpoint}px)`).addEventListener('change', handleMatchMediaChanges);
  }

  clickEvent() {
    const setCookie = this.setCookie.bind(this);
    const handleBreakpointStyling = this.handleBreakpointStyling.bind(this);
    const handleConflictingElements = this.handleConflictingElements.bind(this);
    const removeMatchMediaListener = this.removeMatchMediaListener.bind(this);
    const { button } = this;

    const event = () => {
      fadeOut(this.$container, {
        done() {
          setCookie();
          handleBreakpointStyling();
          handleConflictingElements();
          removeMatchMediaListener();
          button.removeEventListener('click', event);
        },
      });
    };

    this.button.addEventListener('click', event);
  }

  displayConflictingElements() {
    if (this.conflictingElements.length === 0) {
      return;
    }

    this.conflictingElements.forEach(el => {
      if (el.classList.contains(this.cssClasses.conflictElementHidden)) {
        el.classList.remove(this.cssClasses.conflictElementHidden);
      }
    });
  }

  events() {
    this.clickEvent();
    this.$container.classList.remove('gdpr--hidden');

    setTimeout(() => {
      this.handleBreakpointStyling();
      this.handleConflictingElements();
      this.addMatchMediaListener();
      this.$container.classList.add('gdpr--fade-in');

      // Display conflicting elements after 500ms, which is what the gdpr transition duration is set to
      setTimeout(() => {
        this.displayConflictingElements();
      }, 500);

      // Run all of these methods after 500ms, which is what the gdpr transition duration is set to
    }, 500);
  }

  /**
   * This handles multiple conflicting elements on a page, this positions conflicting elements above the gdpr notice
   */
  handleConflictingElements() {
    if (this.conflictingElements.length === 0) {
      return;
    }

    let totalOffsetFromBottom = 0;
    const gdprHeight = parseInt(this.$container.offsetHeight, 10);
    this.conflictingElements.forEach((el, index) => {
      const { gdprOffset } = el.dataset;
      const elementOffsetFromBottom = gdprOffset ? parseInt(gdprOffset, 10) : 0;
      const elementHeight = parseInt(el.offsetHeight, 10);

      // Disable eslint to allow assigning styles to el
      /* eslint-disable */
      if (window.matchMedia(`(min-width: ${this.breakpoint}px)`).matches) {
        // In desktop, calculate bottom positioning without the GDPR element since elements do not stack on top of the
        // GDPR element
        totalOffsetFromBottom = calculateOffsetWithoutGdprElement(el, index, elementOffsetFromBottom, totalOffsetFromBottom, elementHeight);
      } else {
        // In mobile, calculate bottom positioning so that conflicting elements stack on top of the GDPR element
        if (cookieIsSet(this.cookieName)) {
          // If cookie is set, do not display the GDPR element
          // Calculate bottom positioning without the GDPR element
          totalOffsetFromBottom = calculateOffsetWithoutGdprElement(el, index, elementOffsetFromBottom, totalOffsetFromBottom, elementHeight);
        } else {
          // This condition is applied when the gdpr notice is visible
          if (index === 0) {
            // For the first conflicting element
            // The bottom position is the height of the gdpr notice plus the element's offset value from the bottom
            el.style.bottom = `${(gdprHeight + elementOffsetFromBottom) / 16}rem`;
            totalOffsetFromBottom += gdprHeight + elementOffsetFromBottom + elementHeight;
          } else {
            // For other conflicting elements, set the bottom position to the offset value plus the offset from previous elements
            el.style.bottom = `${(totalOffsetFromBottom + elementOffsetFromBottom) / 16}rem`;
            totalOffsetFromBottom += elementOffsetFromBottom + elementHeight;
          }
        }
      }
      /* eslint-enable */
    });
  }

  /**
   * Breakpoint styling needs to be handled in javascript instead of CSS due to unique breakpoints
   */
  handleBreakpointStyling() {
    if (window.matchMedia(`(min-width: ${this.breakpoint}px)`).matches) {
      this.$container.classList.add(this.cssClasses.desktop);
    } else {
      this.$container.classList.remove(this.cssClasses.desktop);
    }
  }

  handleMatchMediaChanges() {
    this.$container.classList.toggle(this.cssClasses.desktop);
    this.handleConflictingElements();
  }

  removeMatchMediaListener() {
    const handleMatchMediaChanges = this.handleMatchMediaChanges.bind(this);
    window.matchMedia(`(min-width: ${this.breakpoint}px)`).removeEventListener('change', handleMatchMediaChanges);
  }

  setCookie() {
    const expirationDate = new Date();

    // Cookie is set to expire in 48 hours
    expirationDate.setDate(expirationDate.getDate() + 2);
    document.cookie = `${this.cookieName}=set; expires=${expirationDate}; path=/`;
  }
}
